// flowchart: https://miro.com/app/board/uXjVMO709fA=/

import * as Sentry from "@sentry/vue";

export class Settings {
  device_supported = true;
  camera_index = null;
  camera_stream_size; // Not default for that value = {height: 2160, width: 3840};

  se_min_resolution = 600;
  se_max_resolution = 2000;

  se_initial_zoom_factor = 2;
  se_zoom_factor = 1;

  se_qr_blur = 86;
  se_marker_blur = 87;

  use_defaults = false;

  resolution_correction_factor = 1;
  source = "default";

  constructor(raw_settings) {
    if (!raw_settings) {
      this.use_defaults = true;
      return;
    }
    if ("source" in raw_settings) {
      this.source = raw_settings.source;
    } else {
      this.source = "web";
    }

    if ("supported" in raw_settings) {
      this.device_supported = raw_settings.supported;
    }
    if ("camera_id" in raw_settings) {
      this.camera_index = raw_settings.camera_id;
    }

    if ("camera_stream_size" in raw_settings) {
      this.camera_stream_size = raw_settings.camera_stream_size;
    }

    if ("zoom_level" in raw_settings) {
      this.se_zoom_factor = raw_settings.zoom_level;
    }

    if ("min_capture_res" in raw_settings) {
      this.se_min_resolution = raw_settings.min_capture_res * this.resolution_correction_factor;
    }
    if ("max_capture_res" in raw_settings) {
      this.se_max_resolution = raw_settings.max_capture_res * this.resolution_correction_factor;
    }

    if ("qr_blur" in raw_settings) {
      this.se_qr_blur = raw_settings.qr_blur;
    }
    if ("marker_blur" in raw_settings) {
      this.se_marker_blur = raw_settings.marker_blur;
    }
  }
}

class SettingsProvider {
  static settings_data;

  static async get_settings() {
    if (this.settings_data) {
      return this.settings_data;
    }

    const device_settings_url = import.meta.env.VITE_DEVICE_SETTINGS_URL;
    let data = await fetch(device_settings_url, {
      method: "GET"
    }).then((response) => response.json());

    this.settings_data = data.settings;
    return this.settings_data;
  }

  static async settings_for_iOS(device_model) {
    const { os } = await this.get_settings();
    for (const [key, value] of Object.entries(os.ios)) {
      if (value.models !== undefined && value.models.includes(device_model)) {
        return new Settings(value.settings);
      }
    }

    // No settings found
    return new Settings();
  }

  static async settings_for_android(device_model) {
    const { os } = await this.get_settings();

    // Check for exact match with upper case. The config generator normalizes keys to uppercase, while the
    // device model from ua-parser-js keeps its natural casing, so uppercase it before lookup.
    const settings = os.android.exact[device_model?.toUpperCase()];
    if (settings !== undefined) {
      return new Settings(settings);
    }

    // Check for regex
    for (const [key, value] of Object.entries(os.android.regex)) {
      if (RegExp(value.regex, "i").test(device_model)) {
        return new Settings(value.settings);
      }
    }

    // No settings found
    return new Settings();
  }
}

export async function getSettings(device_model, is_iOS) {
  try {
    if (is_iOS) {
      return await SettingsProvider.settings_for_iOS(device_model);
    }
    return await SettingsProvider.settings_for_android(device_model);
  } catch (error) {
    Sentry.captureException(new Error(`Error getting settings for device: ${error.message}`));
    return new Settings();
  }
}
